import { OptionType } from 'components/UI/SelectField';
import { APIError } from 'models/errors';

export const NON_CFS = 'non-cfs';
export const CFS = 'cfs';
export const TRUSTED_CFS = 'trusted-cfs';
export const ADAPTER = 'adapter';
export const BIGDATA_INTEGRATION = 'bigdata-integration';

export enum HusqiotBusAttribute {
  SelectedServiceName = 'selectedServiceName',
  ServiceType = 'serviceType',
  DiamServiceName = 'diamServiceName',
  Annotators = 'annotators',
  Validators = 'validators',
  Addresses = 'addresses',
  Authorizers = 'authorizers',
  Acceptors = 'acceptors',
  XmlAddresses = 'xmlAddresses',
  XmlDevDiverts = 'xmlDevDiverts',
  XmlQaDiverts = 'xmlQaDiverts',
  XmlLiveDiverts = 'xmlLiveDiverts',
  XmlSecuritSettings = 'xmlSecuritSettings',
  XmlHusqiotExtensions = 'zmlHusqiotExtensions',
  ServiceName = 'serviceName',
  ContactEmail = 'contactEmail',
}

export enum DivertsAttribute {
  Filter = 'filter',
  Version = 'version',
  Direction = 'direction',
  ServiceName = 'serviceName',
  Dev = 'Dev',
  Qa = 'Qa',
  Live = 'Live',
  Manual = 'Manual',
}

export enum SoftwareAttribute {
  Name = 'name',
  Version = 'version',
  ArticleNumber = 'articleNumber',
  Revision = 'revision',
  Category = 'category',
  FirmwareVersion = 'firmwareVersion',
  BundelId = 'bundelId',
  Pnc = 'pnc',
}

export type HusqiotbusState = {
  config: HusqiotbusConfig;
  basicData: BasicData;
  templates: Template[];
  auditConfig: AuditConfig;
  list: HusqiotbusConfigList;
};

export type HusqiotbusConfig = {
  selectedService?: Service;
  subscriptions: {
    data: Subscription[];
    isLoading: boolean;
    error: APIError | null;
  };
  isLoading: boolean;
  isServiceLoading: boolean;
  isXmlLoading: boolean;
  error: APIError | null;
};

export type Subscription = {
  service: string;
  configs: {
    config: string;
    features?: {
      feature: string;
      resourceIds: string[];
    }[];
    iprIdFilter?: boolean;
  }[];
};

export type SubscriptionSuccess = {
  subscriptions: Subscription[];
};

export type ExistingConfig = {
  name: string;
  content: string;
};
export type HusqiotbusConfigList = {
  data: Service[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  filters: any;
};

export type NameContent = {
  name: string;
  content: string;
};

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface Template extends NameContent {}
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface Config extends NameContent {}

export type XmlFilesContent = {
  addresses: string;
  security: string;
  devDiverts: string | undefined;
  qaDiverts: string | undefined;
  liveDiverts: string | undefined;
  extension: string;
};

export type ConfigSuccess = {
  data: Service[];
  totalCount: number;
};

export type ConfigDetailSuccess = {
  name: string;
};

export type ServiceSuccess = {
  serviceName: string;
};

export type CreateServiceSuccess = {
  serviceName: string;
};

export type TemplatesSuccess = {
  templates: Template[];
};

export type BasicDataSuccess = {
  basicData: BasicData;
};

export type AuditConfigSuccess = {
  auditConfig: AuditConfig;
};

export type UpdateServiceSuccess = {
  serviceType?: string;
  serviceName?: string;
  diamServiceName?: string;
  annotators?: Annotator[];
  authorizers?: Authorizer[];
  validators?: Validator[];
  acceptors?: Acceptor[];
  diverts?: Divert[];
  addresses?: Address[];
};

export type Service = {
  name: string;
  contactEmail?: string;
  type: string;
  diamServiceName: string;
  annotators: Annotator[];
  authorizers: Authorizer[];
  validators: Validator[];
  acceptors: Acceptor[];
  addresses: Address[];
  diverts: Divert[];
  existingDiverts: Divert[];
  xml?: XmlFilesContent;
};

export type CheckboxConfigType = {
  name: string;
  checked: boolean;
  disabled: boolean;
};

export type AuthorizerBasicData = {
  name: string;
  systems?: string[];
  role?: string;
  allSystems?: boolean;
};

export type AnnotatorBasicData = {
  name: string;
  keyAnnotator?: KeyAnnotator;
  systemAnnotator?: SystemAnnotator;
  websocketAnnotator?: WebsocketAnnotator;
  staticAnnotator?: StaticAnnotator;
};

export type KeyAnnotator = {
  keySpace: string;
  keyAttribute: KeyAttribute;
  attributeValues: string[];
};

export type SystemAnnotator = {
  keyAttribute: KeyAttribute;
};

export type WebsocketAnnotator = {
  userPrefix: string;
  keyAttribute: KeyAttribute;
  system: string;
};

export type StaticAnnotator = {
  attribute: string;
  value: string;
};

export type KeyAttribute = {
  value: string;
  attributeContext?: string;
};

export type AcceptorBasicData = {
  name: string;
  attributeContext?: string;
  attributeValue?: string;
  expression?: string;
  system?: string;
};

export type ValidatorBasicData = {
  name: string;
  attributeContext: string;
  ordinality?: string;
  attributeValue: string;
  expression: string;
};

export type RolesBasicData = {
  roleName: string;
  features: {
    featureName: string;
    accessLevel: string;
  }[];
};
export type AddressesBasicData = {
  name: string;
};

export type Address = CheckboxConfigType;
export type Annotator = CheckboxConfigType;

export type Validator = CheckboxConfigType;

export type Authorizer = CheckboxConfigType;

export type Acceptor = CheckboxConfigType;

export type Divert = {
  serviceName: string;
  type: string;
  filter: string;
  environments: string[];
  version: string;
};

export type ServiceType = {
  name: string;
  default: DefaultConfig;
};

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type DefaultConfig = {};

export type ConfigMeta = {
  totalCount: number;
};

export type ServiceConfigResponse = {
  id: string;
  type: string;
  attributes: {
    service: Service;
  };
};

export enum ConfigFilterAttribute {
  name = 'name',
}

export type ConfigFilter = {
  attribute: ConfigFilterAttribute;
  value: string;
};

export type BasicData = {
  systems: System[];
  roles: RolesBasicData[];
  annotators: AnnotatorBasicData[];
  authorizers: AuthorizerBasicData[];
  acceptors: AcceptorBasicData[];
  validators: ValidatorBasicData[];
  serviceTypes: OptionType[];
  addresses: AddressesBasicData[];
};

export type System = {
  name: string;
  authorizer: string;
};

export type AuditConfig = {
  environments: string[];
  excludedAdapters: string[];
};

export type SubscriptionsResponse = {
  data: SubscriptionResponse[];
  meta: { totalCount: number };
  errors?: APIError[];
};

export type SubscriptionResponse = {
  type: string;
  attributes: Subscription[];
};
